import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Icon } from "../lib/Icon";
import { Avatar, StickerCard } from "../lib/components";
import { useAuth } from "../contexts/AuthContext";
import { useCatalog } from "../contexts/CatalogContext";
import { useInventory } from "../contexts/InventoryContext";
import { useToast } from "../lib/toast";
import { ApiError } from "../api/client";
import {
  cancelListing,
  createListing,
  fetchListings,
  markSold,
  uploadListingPhoto,
  type CreateListingPayload,
  type Listing,
  type ListingType,
} from "../api/market";
import { sendMessage } from "../api/messages";
import { fetchUsers } from "../api/admin";
import { api } from "../api/client";

interface Props {
  openChat: (userId: number) => void;
}

const TYPE_META: Record<ListingType | "all", { label: string; chip: string; icon: string; emoji: string }> = {
  all: { label: "Todo", chip: "", icon: "store", emoji: "📦" },
  sale: { label: "Venta", chip: "primary", icon: "money", emoji: "💰" },
  trade: { label: "Cambio", chip: "cobalt", icon: "swap", emoji: "🔄" },
  auction: { label: "Subasta", chip: "magenta", icon: "hammer", emoji: "🔨" },
  gift: { label: "Regalo", chip: "amber", icon: "gift", emoji: "🎁" },
};

export function MarketScreen({ openChat }: Props) {
  const { user: me } = useAuth();
  const { stickerById, teamById } = useCatalog();
  const { ownership } = useInventory();
  const toast = useToast();

  const [filter, setFilter] = useState<ListingType | "all">("all");
  const [search, setSearch] = useState("");
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPublish, setShowPublish] = useState(false);
  const [sellerNames, setSellerNames] = useState<Record<number, string>>({});

  const reload = () => {
    setLoading(true);
    fetchListings({ type: filter === "all" ? undefined : filter, limit: 100 })
      .then(setListings)
      .catch((e) => toast(`Error cargando mercado: ${e}`))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Filtrar por búsqueda (cliente — la API ya filtra por type)
  const visibleListings = useMemo(() => {
    if (!search.trim()) return listings;
    const q = search.toLowerCase().trim();
    return listings.filter((l) => {
      const s = stickerById(l.sticker_id);
      if (!s) return false;
      const t = teamById(s.team_id);
      const haystack = `${s.name} ${s.num} ${t?.code ?? ""} ${t?.name ?? ""} ${l.note ?? ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [listings, search, stickerById, teamById]);

  // Resolver nombres de vendedores (no expuestos en el endpoint público)
  // Para v0 hago una llamada admin SOLO si soy admin; sino muestro "Usuario #N".
  // Mejor enfoque: agregar /users/{id} público — pero por simplicidad, hacemos lookup desde matches o thread peer si existe.
  useEffect(() => {
    const sellerIds = [...new Set(listings.map((l) => l.seller_id).filter((id) => id !== me?.id))];
    if (sellerIds.length === 0) return;
    // Best effort: para cada seller_id que no conozco, hago un "buscar usuario por id" via /admin/users si soy admin.
    // Si no soy admin, no muestro nombre — solo "Usuario #N". El nombre real aparecerá al abrir chat.
    if (me?.role === "admin" || me?.role === "super_admin") {
      fetchUsers({ limit: 200 }).then((users) => {
        const map: Record<number, string> = {};
        for (const u of users) map[u.id] = u.name;
        setSellerNames(map);
      }).catch(() => null);
    }
  }, [listings, me]);

  const myDuplicates = useMemo(() => {
    return Object.entries(ownership)
      .filter(([_, count]) => count >= 2)
      .map(([id]) => Number(id));
  }, [ownership]);

  const myAny = useMemo(() => {
    return Object.entries(ownership)
      .filter(([_, count]) => count >= 1)
      .map(([id]) => Number(id));
  }, [ownership]);

  const allStickerIds = useMemo(() => stickerById ? [] : [], []); // dummy — ver catalog en dialog

  const handleContactSeller = async (listing: Listing) => {
    if (!me) return;
    const sticker = stickerById(listing.sticker_id);
    if (!sticker) return;
    const initial = `Hola! Vi tu publicación de ${sticker.num} (${sticker.name}). ${
      listing.type === "sale" ? `Me interesa, ¿sigue disponible por $${listing.price.toLocaleString("es-CO")}?` :
      listing.type === "trade" ? "Me interesa el cambio, ¿qué pides?" :
      listing.type === "auction" ? `Me interesa la subasta. ${listing.current_bid ? `Vi la puja en $${listing.current_bid.toLocaleString("es-CO")}.` : ""}` :
      "Me interesa el regalo, ¿coordinamos?"
    }`;
    try {
      await sendMessage(listing.seller_id, initial);
      toast("Mensaje enviado · ve al chat");
      openChat(listing.seller_id);
    } catch (e) {
      toast(`Error: ${e instanceof ApiError ? e.message : e}`);
    }
  };

  const handleCancel = async (id: number) => {
    if (!confirm("¿Cancelar esta publicación?")) return;
    try {
      await cancelListing(id);
      toast("Publicación cancelada");
      reload();
    } catch (e) {
      toast(`Error: ${e}`);
    }
  };

  const handleMarkSold = async (id: number) => {
    if (!confirm("¿Marcar como vendida/intercambiada?")) return;
    try {
      await markSold(id);
      toast("Marcada como completada");
      reload();
    } catch (e) {
      toast(`Error: ${e}`);
    }
  };

  return (
    <div className="page">
      <div style={{ display: "flex", alignItems: "flex-end", marginBottom: 16, gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: 32, fontWeight: 800, margin: 0, letterSpacing: "-0.02em" }}>Mercado</h1>
          <p style={{ color: "var(--fg-muted)", margin: "4px 0 0" }}>Publica tus repetidas para vender, cambiar o regalar. Coordina con el comprador por chat.</p>
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn btn-primary" onClick={() => setShowPublish(true)}>
          <Icon name="plus" size={16} /> Publicar lámina
        </button>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <div className="seg-control" style={{ flex: "1 1 auto" }}>
          {(["all", "sale", "trade", "auction", "gift"] as const).map((k) => (
            <button key={k} className={filter === k ? "active" : ""} onClick={() => setFilter(k)}>
              {TYPE_META[k].emoji} {TYPE_META[k].label}
            </button>
          ))}
        </div>
        <div className="search-box" style={{ flex: "1 1 240px", minWidth: 200 }}>
          <Icon name="search" size={16} />
          <input
            placeholder='Buscar: jugador, "POR", "Portugal", "12"…'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--fg-muted)" }}>Cargando…</div>
      ) : visibleListings.length === 0 && search ? (
        <div className="card" style={{ textAlign: "center", padding: 32 }}>
          <Icon name="search" size={28} />
          <h3 style={{ margin: "12px 0 6px", fontFamily: "var(--font-display)" }}>Sin resultados para "{search}"</h3>
          <p style={{ color: "var(--fg-muted)", fontSize: 14, margin: 0 }}>
            Prueba con otro término o quita el filtro.
          </p>
        </div>
      ) : listings.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 40 }}>
          <Icon name="store" size={32} />
          <h3 style={{ margin: "12px 0 6px", fontFamily: "var(--font-display)" }}>Aún no hay publicaciones</h3>
          <p style={{ color: "var(--fg-muted)", fontSize: 14, margin: "0 0 16px" }}>
            ¡Sé el primero en publicar! Si tienes láminas repetidas, ofrécelas en venta, cambio o regalo.
          </p>
          <button className="btn btn-primary" onClick={() => setShowPublish(true)}>
            <Icon name="plus" size={14} /> Publicar mi primera lámina
          </button>
        </div>
      ) : (
        <div className="market-grid">
          {visibleListings.map((l) => {
            const sticker = stickerById(l.sticker_id);
            const team = sticker ? teamById(sticker.team_id) : null;
            if (!sticker) return null;
            const meta = TYPE_META[l.type];
            const isMine = me && l.seller_id === me.id;
            const sellerName = sellerNames[l.seller_id] ?? (isMine ? "Tú" : `Usuario #${l.seller_id}`);
            return (
              <div key={l.id} className="listing">
                <div className="listing-thumb">
                  {l.photo_url ? (
                    <img
                      src={l.photo_url}
                      alt={`Foto del lote ${sticker.num}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
                    />
                  ) : (
                    <StickerCard sticker={sticker} teamColor={team?.color} teamCode={team?.code} count={1} showDup={false} />
                  )}
                </div>
                <div className="listing-info">
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div className={"chip " + meta.chip}>
                      <Icon name={meta.icon} size={11} /> {meta.label}
                    </div>
                    <div className="tiny muted">
                      {l.items.length > 1 ? `${l.items.length} láminas · #${sticker.num}` : `#${sticker.num}`}
                    </div>
                  </div>
                  <div className="listing-name">
                    {l.items.length > 1
                      ? `Lote: ${l.items.slice(0, 3).map((it) => it.sticker_num).join(" + ")}${l.items.length > 3 ? ` +${l.items.length - 3}` : ""}`
                      : sticker.name}
                  </div>
                  {/* Lista compacta de items cuando es bundle */}
                  {l.items.length > 1 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                      {l.items.map((it) => (
                        <span key={it.sticker_id} className="chip" style={{ fontSize: 9, padding: "1px 5px", background: it.is_main ? "var(--primary-soft)" : "var(--bg-subtle)", color: it.is_main ? "var(--primary)" : "var(--fg-muted)" }} title={`${it.sticker_num} · ${it.sticker_name}`}>
                          {it.sticker_num}
                        </span>
                      ))}
                    </div>
                  )}
                  {l.note && (
                    <div style={{ background: "var(--bg-subtle)", padding: 8, borderRadius: 6, fontSize: 12, color: "var(--fg)", marginTop: 4 }}>
                      💬 {l.note}
                    </div>
                  )}
                  <div className="listing-meta">
                    <Avatar user={isMine ? me : null} />
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--fg)" }}>{sellerName}</div>
                      <div className="tiny muted">{relativeDate(l.created_at)}</div>
                    </div>
                  </div>
                  <div className="listing-meta tiny">
                    Estado: <b style={{ color: "var(--fg)" }}>{l.condition}</b>
                  </div>
                  {l.type === "sale" && <div className="listing-price">${l.price.toLocaleString("es-CO")}</div>}
                  {l.type === "auction" && (
                    <div>
                      <div className="listing-price">${(l.current_bid || l.price).toLocaleString("es-CO")}</div>
                      {l.ends_at && <div className="auction-timer"><Icon name="flame" size={11} /> Termina: {new Date(l.ends_at).toLocaleString("es-CO")}</div>}
                    </div>
                  )}
                  {l.type === "trade" && <div className="listing-price" style={{ color: "var(--accent-cobalt)" }}>Por cambio</div>}
                  {l.type === "gift" && <div className="listing-price" style={{ color: "var(--accent-amber)" }}>GRATIS</div>}
                </div>
                <div className="listing-actions">
                  {isMine ? (
                    <>
                      <button className="btn btn-secondary btn-sm" style={{ flex: 1, justifyContent: "center" }} onClick={() => handleCancel(l.id)}>
                        Cancelar
                      </button>
                      <button className="btn btn-primary btn-sm" style={{ flex: 1, justifyContent: "center" }} onClick={() => handleMarkSold(l.id)}>
                        ✓ Completada
                      </button>
                    </>
                  ) : (
                    <button className="btn btn-primary btn-sm" style={{ flex: 1, justifyContent: "center" }} onClick={() => handleContactSeller(l)}>
                      <Icon name="chat" size={14} /> Contactar
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showPublish && (
        <PublishDialog
          onClose={() => setShowPublish(false)}
          onCreated={() => { setShowPublish(false); reload(); toast("Publicación creada ✓"); }}
          myDuplicates={myDuplicates}
          myAny={myAny}
        />
      )}
    </div>
  );
}

interface PublishDialogProps {
  onClose: () => void;
  onCreated: () => void;
  myDuplicates: number[];
  myAny: number[];
}

type PoolMode = "duplicates" | "owned" | "all";

function PublishDialog({ onClose, onCreated, myDuplicates, myAny }: PublishDialogProps) {
  const { stickers, stickerById, teamById } = useCatalog();
  const toast = useToast();

  const initialPool: PoolMode =
    myDuplicates.length > 0 ? "duplicates" : myAny.length > 0 ? "owned" : "all";
  const [pool, setPool] = useState<PoolMode>(initialPool);

  const eligibleStickerIds = useMemo(() => {
    if (pool === "duplicates") return myDuplicates;
    if (pool === "owned") return myAny;
    return stickers.map((s) => s.id);
  }, [pool, myDuplicates, myAny, stickers]);

  // Multi-select: array ordenado de IDs. El PRIMERO es el "main" (la portada
  // del listing). El user puede reordenar haciendo click en "Hacer principal".
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [type, setType] = useState<ListingType>("trade");
  const [price, setPrice] = useState<string>("");
  const [condition, setCondition] = useState("Buena");
  const [note, setNote] = useState("");
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");

  const switchPool = (p: PoolMode) => {
    setPool(p);
    setSearch("");
  };

  const filteredStickerIds = useMemo(() => {
    if (!search) return eligibleStickerIds.slice(0, 80);
    const q = search.toLowerCase().trim();
    return eligibleStickerIds.filter((id) => {
      const s = stickerById(id);
      if (!s) return false;
      const t = teamById(s.team_id);
      const haystack = `${s.name} ${s.num} ${t?.code ?? ""} ${t?.name ?? ""}`.toLowerCase();
      return haystack.includes(q);
    }).slice(0, 80);
  }, [search, eligibleStickerIds, stickerById, teamById]);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const makeMain = (id: number) => {
    setSelectedIds((prev) => [id, ...prev.filter((x) => x !== id)]);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (selectedIds.length === 0) {
      toast("Seleccioná al menos una lámina");
      return;
    }
    if (type === "sale" && (!price || Number(price) <= 0)) {
      toast("Define un precio para la venta");
      return;
    }
    setSubmitting(true);
    try {
      const [mainId, ...extras] = selectedIds;
      const payload: CreateListingPayload = {
        sticker_id: mainId,
        type,
        price: type === "sale" || type === "auction" ? Number(price) || 0 : 0,
        condition,
        note: note.trim() || undefined,
        extra_sticker_ids: extras,
      };
      const listing = await createListing(payload);
      // Si el user adjuntó foto, la subimos después de crear el listing.
      if (photoFile) {
        try {
          await uploadListingPhoto(listing.id, photoFile);
        } catch (e) {
          toast(`Listing creado, pero la foto falló: ${e instanceof ApiError ? e.message : e}`);
        }
      }
      onCreated();
    } catch (e) {
      toast(`Error: ${e instanceof ApiError ? e.message : e}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog onClose={onClose} title="Publicar lámina">
      <form onSubmit={submit} className="col" style={{ gap: 16 }}>
        <div className="field">
          <label>¿De qué láminas quieres elegir?</label>
          <div className="seg-control">
            <button
              type="button"
              className={pool === "duplicates" ? "active" : ""}
              onClick={() => switchPool("duplicates")}
              disabled={myDuplicates.length === 0}
            >
              Mis repetidas ({myDuplicates.length})
            </button>
            <button
              type="button"
              className={pool === "owned" ? "active" : ""}
              onClick={() => switchPool("owned")}
              disabled={myAny.length === 0}
            >
              Todas mías ({myAny.length})
            </button>
            <button
              type="button"
              className={pool === "all" ? "active" : ""}
              onClick={() => switchPool("all")}
            >
              Todo el catálogo ({stickers.length})
            </button>
          </div>
          <div className="hint" style={{ marginTop: 6 }}>
            {pool === "all" && "Útil si solo usás PaniniNilabs para vender sin llevar tu álbum digital. Coordinas la entrega con el comprador."}
            {pool === "duplicates" && "Las que tienes más de una vez — ideales para intercambiar o vender."}
            {pool === "owned" && "Cualquier lámina que marcaste como tuya."}
          </div>
        </div>

        <div className="field">
          <label>Láminas en este listing ({selectedIds.length})</label>
          <div className="hint" style={{ marginBottom: 6 }}>
            Podés combinar varias en un solo lote (ej. CR7 + Messi + Copa por $50.000).
            La PRIMERA seleccionada queda como portada del anuncio.
          </div>

          {/* Lista de seleccionadas con orden + remove */}
          {selectedIds.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
              {selectedIds.map((id, i) => {
                const s = stickerById(id);
                const t = s ? teamById(s.team_id) : null;
                return (
                  <div key={id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", background: i === 0 ? "var(--primary-soft)" : "var(--bg-subtle)", borderRadius: 6, fontSize: 12 }}>
                    <span style={{ width: 4, height: 18, background: t?.color || "#999", borderRadius: 2, flexShrink: 0 }} />
                    {i === 0 && <span className="chip primary" style={{ fontSize: 9 }}>portada</span>}
                    <b>{s?.num || "?"}</b>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s?.name || ""}</span>
                    {i !== 0 && (
                      <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: "2px 6px" }} onClick={() => makeMain(id)}>
                        Hacer portada
                      </button>
                    )}
                    <button type="button" className="icon-btn" onClick={() => toggleSelect(id)} aria-label="Quitar">
                      <Icon name="x" size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <input
            placeholder="Buscar por jugador, número, país…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div style={{ marginTop: 8, maxHeight: 220, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8, padding: 6 }}>
            {filteredStickerIds.length === 0 ? (
              <div style={{ padding: 12, color: "var(--fg-muted)", fontSize: 13, textAlign: "center" }}>Sin resultados</div>
            ) : (
              filteredStickerIds.map((id) => {
                const s = stickerById(id);
                if (!s) return null;
                const t = teamById(s.team_id);
                const isSelected = selectedIds.includes(id);
                return (
                  <button
                    type="button"
                    key={id}
                    onClick={() => toggleSelect(id)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "6px 10px",
                      borderRadius: 6,
                      border: 0,
                      background: isSelected ? "var(--primary-soft)" : "transparent",
                      outline: isSelected ? "2px solid var(--primary)" : undefined,
                      cursor: "pointer",
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      fontSize: 13,
                      marginBottom: 2,
                    }}
                  >
                    <span style={{
                      width: 16, height: 16, borderRadius: 3, flexShrink: 0,
                      border: "2px solid " + (isSelected ? "var(--primary)" : "var(--border-strong)"),
                      background: isSelected ? "var(--primary)" : "transparent",
                      display: "grid", placeItems: "center", color: "white",
                    }}>
                      {isSelected && <Icon name="check" size={9} />}
                    </span>
                    <span style={{ width: 4, height: 14, background: t?.color || "#999", borderRadius: 2 }} />
                    <b>{s.num}</b>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
                    {s.is_legendary && <span className="chip amber" style={{ fontSize: 9, padding: "1px 4px" }}>LEG</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="field">
          <label>Foto del lote (opcional)</label>
          <div className="hint" style={{ marginBottom: 6 }}>
            Subí una foto real de las láminas (estado, autenticidad). Max 5MB.
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
          />
          {photoFile && (
            <div className="tiny" style={{ marginTop: 4, color: "var(--primary)" }}>
              <Icon name="check" size={11} /> {photoFile.name} ({Math.round(photoFile.size / 1024)} KB)
            </div>
          )}
        </div>

        <div className="field">
          <label>Tipo de publicación</label>
          <div className="seg-control">
            {(["sale", "trade", "auction", "gift"] as const).map((t) => (
              <button key={t} type="button" className={type === t ? "active" : ""} onClick={() => setType(t)}>
                {TYPE_META[t].emoji} {TYPE_META[t].label}
              </button>
            ))}
          </div>
        </div>

        {(type === "sale" || type === "auction") && (
          <div className="field">
            <label>{type === "sale" ? "Precio (COP)" : "Precio inicial (COP)"}</label>
            <input
              type="number"
              min={0}
              step={500}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder={type === "sale" ? "25000" : "5000"}
            />
            <div className="hint">Sin pasarela aún · coordinas el pago con el comprador por chat.</div>
          </div>
        )}

        <div className="field">
          <label>Estado de la lámina</label>
          <select value={condition} onChange={(e) => setCondition(e.target.value)}>
            <option>Perfecta</option>
            <option>Buena</option>
            <option>Doblada</option>
            <option>Marcada</option>
          </select>
        </div>

        <div className="field">
          <label>Descripción (opcional)</label>
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={
              type === "trade" ? "Ej: Cambio por Messi · Negociable · Recibo ofertas" :
              type === "sale" ? "Ej: Negociable · Acepto Nequi/Daviplata · Envío incluido" :
              type === "auction" ? "Ej: Termina mañana · Mejor oferta gana" :
              "Ej: Para quien complete primero su grupo · Solo Bogotá"
            }
            maxLength={500}
          />
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn btn-primary" disabled={submitting || selectedIds.length === 0}>
            {submitting ? "Publicando…" : `Publicar ${selectedIds.length > 1 ? `(${selectedIds.length} láminas)` : ""}`}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function Dialog({ children, onClose, title }: { children: React.ReactNode; onClose: () => void; title: string }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        display: "grid", placeItems: "center", zIndex: 999, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg)", borderRadius: 14, maxWidth: 520, width: "100%",
          maxHeight: "90vh", overflowY: "auto", padding: 20,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 800, margin: 0 }}>{title}</h2>
          <div style={{ flex: 1 }} />
          <button className="icon-btn" onClick={onClose} aria-label="Cerrar"><Icon name="x" size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function relativeDate(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `hace ${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `hace ${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `hace ${day}d`;
  return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short" });
}
